import { loadGoogleMaps } from '@theme/google-maps';

const DEFAULT_ZOOM = 15;

if (!customElements.get('store-detail-map')) {
  customElements.define(
    'store-detail-map',
    // Progressively enhances a single store's map: the element ships with a Google Maps <iframe>
    // fallback in its markup, and this upgrades it to a JS Maps API map so the store's marker can
    // use the theme's custom pin icon (which the iframe embed can't render). If the API key or
    // coordinates are missing, or Maps fails to load, the iframe fallback is left in place.
    class StoreDetailMap extends HTMLElement {
      /** Loads Google Maps and upgrades the iframe fallback to a JS map, when a key and valid coordinates are present. */
      connectedCallback() {
        const apiKey = this.getAttribute('maps-api-key');
        const lat = Number(this.getAttribute('data-lat'));
        const lng = Number(this.getAttribute('data-lng'));

        if (!apiKey || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

        loadGoogleMaps(apiKey)
          .then(() => this.#initMap({ lat, lng }))
          .catch((error) => console.warn('Store detail map: failed to load Google Maps.', error));
      }

      /**
       * Replaces the iframe fallback with a Google Map centred on the store, marked with the custom pin.
       * @param {{ lat: number, lng: number }} position - The store's coordinates.
       */
      #initMap(position) {
        // If focus was on the iframe fallback (e.g. a keyboard user reached it before Maps loaded),
        // move it onto the replacement canvas so it isn't silently dropped to <body>.
        const hadFocus = this.contains(document.activeElement);

        const canvas = document.createElement('div');
        canvas.className = 'store-detail__map-canvas';
        this.replaceChildren(canvas);

        if (hadFocus) {
          canvas.setAttribute('tabindex', '-1');
          canvas.focus();
        }

        const map = new google.maps.Map(canvas, {
          center: position,
          zoom: DEFAULT_ZOOM,
        });

        const markerIconUrl = this.getAttribute('marker-icon-url');
        new google.maps.Marker({
          map,
          position,
          title: this.getAttribute('data-title') ?? '',
          icon: markerIconUrl
            ? {
                url: markerIconUrl,
                scaledSize: new google.maps.Size(32, 32),
                anchor: new google.maps.Point(16, 32),
              }
            : undefined,
        });
      }
    }
  );
}
